import { z } from "zod";

/**
 * THE HELP DESK'S WIRE SHAPES (wave 14a).
 *
 * A person writing to us is not a knock and is not a doorman turn: it is
 * correspondence, addressed to the company rather than to a door. That is the
 * whole reason it needs its own vocabulary — every other message shape in this
 * package belongs to the knock lifecycle, and reusing one of them would put
 * support mail inside a conversation model that has a gate, a standing and a
 * price.
 *
 * The shapes live here rather than in apps/api because THREE services read
 * them: the api writes the mirror, the backoffice reads it, and the SPA renders
 * the form and the list. Chatwoot's own shapes never appear in any of the
 * three — they stop at `adapters/real/support.ts`, which is what the port is
 * for.
 */

/** Subject and body bounds. A support message is a paragraph, not a document. */
export const SUPPORT_SUBJECT_MAX = 120;
export const SUPPORT_BODY_MAX = 4_000;

/**
 * How many conversations one account may open in a day (rolling 24h window).
 * Not a punishment: somebody with five open threads is somebody a person should
 * answer, and a sixth in the same day is a form being held down.
 */
export const SUPPORT_PER_DAY = 5;

/** The address a person may write to instead of using the form. */
export const SUPPORT_ADDRESS = "support@nob.me";

/**
 * WHERE THEY PRESSED "GET HELP", and nothing more. It rides into Chatwoot as a
 * PRIVATE NOTE so the operator opens the thread already knowing which room the
 * person was standing in; it is never shown back to the person, and it carries
 * an id rather than any content — a knock's words are the knock's, and a
 * support thread is not a way around that.
 */
export const SupportContextKind = z.enum(["knock", "plan", "door"]);
export type SupportContextKind = z.infer<typeof SupportContextKind>;

export const SupportContext = z.object({
  kind: SupportContextKind,
  id: z.string().trim().max(64).optional(),
});
export type SupportContext = z.infer<typeof SupportContext>;

/** What the in-app form posts. */
export const OpenSupportRequest = z.object({
  subject: z.string().trim().min(1).max(SUPPORT_SUBJECT_MAX),
  body: z.string().trim().min(1).max(SUPPORT_BODY_MAX),
  context: SupportContext.optional(),
});
export type OpenSupportRequest = z.infer<typeof OpenSupportRequest>;

/** How a thread got here. `other` is Chatwoot's own channels we did not open. */
export const SupportOrigin = z.enum(["in_app", "email", "other"]);
export type SupportOrigin = z.infer<typeof SupportOrigin>;

/**
 * THE PERSON'S OWN VIEW — AN ALLOWLIST, AND IT CARRIES NO BODIES.
 *
 * The same discipline as `toSenderView`: what is listed is named here, and a
 * field added to the mirror does not appear on this list by arriving. Bodies
 * are deliberately absent and the screen says why — the answer arrives by mail
 * at the verified contact address, so a second, silent copy of the
 * correspondence inside the app would be a second place for it to be wrong.
 */
export const SupportConversationView = z.object({
  id: z.string(),
  subject: z.string().nullable(),
  status: z.string(),
  origin: SupportOrigin,
  opened_at: z.string(),
  last_message_at: z.string().nullable(),
});
export type SupportConversationView = z.infer<typeof SupportConversationView>;

export const SupportConversationList = z.object({
  conversations: z.array(SupportConversationView),
  /** Where the answer will land, so the screen states it rather than guessing. */
  replies_to: z.string().nullable(),
  support_address: z.string(),
});
export type SupportConversationList = z.infer<typeof SupportConversationList>;
